import { addCalendarDays } from '../zonedTime';
import { fill, t } from '../strings';
import { weekStart } from '../pages/calendarGrid';

/**
 * 🔒 The on-call roster and its after-hours alert (piece 29; T390, the owner's
 * answer to P29-2): a primary and a backup for each calendar day in the
 * Organization's zone, Swap exchanging two places in one save, and the alert an
 * after-hours emergency sends the day's primary, then the backup once
 * `oncall.backupAfterMinutes` pass unacknowledged (decision 1640). These are
 * the rules and the words, which need no database. 🔨 The roster alerts a
 * person and dispatches nobody.
 */

/** 🔒 Decision 1640: the minutes an unacknowledged alert waits before the day's backup is alerted, seeded at 15. */
export const ON_CALL_BACKUP_AFTER_KEY = 'oncall.backupAfterMinutes';
export const ON_CALL_BACKUP_AFTER_DEFAULT = 15;
/** A day's worth of minutes: an alert unanswered for longer has outlived the day it was sent on. */
export const ON_CALL_BACKUP_AFTER_MAX = 1440;

/** A whole number of minutes from 1 to 1,440, never blank, because the escalation reads it on every tick. */
export function backupAfterMinutesProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= ON_CALL_BACKUP_AFTER_MAX
    ? null
    : `the backup is alerted after a whole number of minutes from 1 to ${ON_CALL_BACKUP_AFTER_MAX}`;
}

/** The setting as read: a stored whole number in range, else the seeded 15. */
export function backupAfterMinutesOf(value: unknown): number {
  return backupAfterMinutesProblem(value) === null ? (value as number) : ON_CALL_BACKUP_AFTER_DEFAULT;
}

/** The two places a roster day holds, and the role an alert row names. */
export const ON_CALL_ROLES = ['Primary', 'Backup'] as const;
export type OnCallRole = (typeof ON_CALL_ROLES)[number];

/** The activity feed entry's type for an alert, as the While You Were Gone tile groups it. */
export const ON_CALL_ALERT_ENTRY_TYPE = 'onCall.alert';

/** The query parameter the alert's link carries, naming the alert a tap acknowledges. */
export const ON_CALL_ALERT_PARAM = 'onCallAlert';

/** 🔒 Decision 4002: the query parameter a link names a document page's view by, opening it once without remembering it. */
export const DOCUMENT_VIEW_PARAM = 'view';

/** 🔒 T2398: the job's Details view, where the in-app notice on the dashboard's feed opens; the viewer's own unacknowledged alert and its Acknowledge button stand in that panel. */
export function onCallNoticePath(workOrderId: string): string {
  return `/work-orders/${workOrderId}?${DOCUMENT_VIEW_PARAM}=details`;
}

/** 🔒 Decision 4002: the alert's link, the job's Details view, where its On-Call panel stands, naming the alert a tap acknowledges. */
export function onCallLinkPath(workOrderId: string, alertId: string): string {
  return `/work-orders/${workOrderId}?${DOCUMENT_VIEW_PARAM}=details&${ON_CALL_ALERT_PARAM}=${alertId}`;
}

/** One place on the roster: a day and which of its two people. */
export interface OnCallPlace {
  onDate: string;
  role: OnCallRole;
}

/** A person a roster place can name: an employment at our own company, by name. */
export interface OnCallPerson {
  employmentId: string;
  name: string;
}

/**
 * 🔒 Decision 4160: where a drawn day's two names came from. Saved is the day's
 * own row naming somebody; standing is its weekday's standing names filling a
 * day with none of its own; none is a day neither names anybody on.
 */
export const ON_CALL_DAY_SOURCES = ['saved', 'standing', 'none'] as const;
export type OnCallDaySource = (typeof ON_CALL_DAY_SOURCES)[number];

/** One day of the week the roster draws: its two places as drawn, a gap where either is empty, and where the names came from. */
export interface OnCallWeekDay {
  onDate: string;
  primary: OnCallPerson | null;
  backup: OnCallPerson | null;
  gap: boolean;
  source: OnCallDaySource;
}

/** One weekday of the standing week (decision 4160), 0 for Sunday, with its two default places and a gap where either is empty. */
export interface OnCallStandingWeekday {
  weekday: number;
  primary: OnCallPerson | null;
  backup: OnCallPerson | null;
  gap: boolean;
}

/** The Standing Week section's read: the seven weekdays from Sunday, who can be named, and whether the viewer saves. */
export interface OnCallStandingAnswer {
  days: OnCallStandingWeekday[];
  people: OnCallPerson[];
  canEdit: boolean;
}

/** The roster page's read: the week, today in the Organization's zone, who can be named, and whether the viewer saves. */
export interface OnCallWeekAnswer {
  start: string;
  today: string;
  zone: string;
  days: OnCallWeekDay[];
  people: OnCallPerson[];
  canEdit: boolean;
}

/** A day's save: both places, either empty. */
export interface OnCallDayInput {
  primaryEmploymentId: string | null;
  backupEmploymentId: string | null;
}

/** Swap's body: the two places whose people trade. */
export interface OnCallSwapInput {
  a: OnCallPlace;
  b: OnCallPlace;
}

export function isOnCallRole(value: unknown): value is OnCallRole {
  return typeof value === 'string' && (ON_CALL_ROLES as readonly string[]).includes(value);
}

/** True for a `YYYY-MM-DD` day that is a real calendar date. */
export function isRosterDay(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return new Date(`${value}T00:00:00.000Z`).toISOString().slice(0, 10) === value;
}

/** 🔒 The week the roster draws: the Sunday the calendar's own grid starts on, then the six days after it. */
export function onCallWeek(anyDay: string): string[] {
  const first = weekStart(anyDay);
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(first, index));
}

/** 🔒 The weekday a roster day falls on, 0 for Sunday through 6 for Saturday, read off the calendar date alone. */
export function rosterWeekday(onDate: string): number {
  return new Date(`${onDate}T00:00:00.000Z`).getUTCDay();
}

/** True for a standing weekday, a whole number from 0 for Sunday to 6 for Saturday. */
export function isStandingWeekday(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6;
}

type OnCallPair = { primaryEmploymentId: string | null; backupEmploymentId: string | null };

/** A pair naming anybody: a row naming nobody is no day of its own (decision 4160). */
export function namesAnybody(pair: OnCallPair | null | undefined): boolean {
  return !!pair && (!!pair.primaryEmploymentId || !!pair.backupEmploymentId);
}

/**
 * 🔒 Decision 4160: a day reads its own row when that row names anybody, else
 * its weekday's standing names, and names nobody where neither does. The
 * standing pair is the one already held to the people running on that day.
 */
export function onCallDaySource(own: OnCallPair | null | undefined, standing: OnCallPair | null | undefined): OnCallDaySource {
  if (namesAnybody(own)) return 'saved';
  return namesAnybody(standing) ? 'standing' : 'none';
}

/** 🔒 A day's pair: two different people where both are set. Either may be empty, which the page draws as a gap. */
export function onCallDayProblem(primaryEmploymentId: string | null, backupEmploymentId: string | null): string | null {
  if (primaryEmploymentId && backupEmploymentId && primaryEmploymentId === backupEmploymentId) return 'the primary and the backup are two different people';
  return null;
}

/** A day with either place empty is a gap: nobody answers, or nobody stands behind whoever does. */
export function isOnCallGap(day: { primaryEmploymentId: string | null; backupEmploymentId: string | null } | null | undefined): boolean {
  return !day?.primaryEmploymentId || !day.backupEmploymentId;
}

/**
 * 🔒 Swap (P29-2): the two places' people trade, a day's primary and backup or
 * one person's day with another person's. Refused where the two places are one,
 * where either is empty, or where a day would end with one person in both places.
 */
export function swapProblem(a: OnCallPlace, b: OnCallPlace, holders: { a: string | null; b: string | null }, after: readonly { primaryEmploymentId: string | null; backupEmploymentId: string | null }[]): string | null {
  if (a.onDate === b.onDate && a.role === b.role) return 'a swap trades two different places';
  if (!holders.a || !holders.b) return 'a swap trades two people’s places; both places need somebody in them';
  if (holders.a === holders.b) return 'both places already hold the same person';
  for (const day of after) {
    const problem = onCallDayProblem(day.primaryEmploymentId, day.backupEmploymentId);
    if (problem) return problem;
  }
  return null;
}

/** 🔒 Decision 1640: a primary's alert unacknowledged this long goes to the backup. */
export function backupDue(sentAt: Date | string, now: Date, minutes: number): boolean {
  return now.getTime() - new Date(sentAt).getTime() >= minutes * 60_000;
}

/** What an alert names (piece 29): the customer, the site, the fault and the job, with the link a tap acknowledges. */
export interface OnCallAlertFacts {
  workOrderNumber: string;
  customerName: string;
  siteName: string;
  fault: string;
  role: OnCallRole;
  link: string;
}

/**
 * 🔒 The alert's words: the feed's sentence and the text. Both name the job,
 * the customer, the site and the fault, and the text carries the link, whose
 * tap acknowledges. A backup's alert says the primary has not answered.
 */
export function onCallAlertWords(facts: OnCallAlertFacts): { summary: string; text: string } {
  const lead = t(facts.role === 'Backup' ? 'workOrder.onCall.alertWords.leadBackup' : 'workOrder.onCall.alertWords.lead');
  const where = facts.siteName ? fill(t('workOrder.onCall.alertWords.atSite'), { customer: facts.customerName, site: facts.siteName }) : facts.customerName;
  const fault = facts.fault.trim() ? fill(t('workOrder.onCall.alertWords.fault'), { fault: facts.fault.trim() }) : '';
  const summary = fill(t('workOrder.onCall.alertWords.summary'), { lead, number: facts.workOrderNumber, where, fault });
  return { summary, text: fill(t('workOrder.onCall.alertWords.text'), { summary, link: facts.link }) };
}
